import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, formatDate, formatNumber } from '../ui';
import {
  INVENTORY_STATUSES,
  InventoryProgress,
  inventoryStatusClass,
  progressPct,
  useInventoryRoutes
} from '../components/inventoryUi';
import {
  buildInventorySiteOptions,
  inventorySessionDisplayName,
  parseInventorySiteKey
} from '../components/inventorySites';

const copy = PHARMACY_COPY.INVENTORY;

function nextActionLabel(status) {
  if (status === 'COUNTING') return copy.NEXT_COUNTING;
  if (status === 'CLOSED') return copy.NEXT_CLOSED;
  if (status === 'CANCELLED') return copy.NEXT_CANCELLED;
  return '';
}

export default function PharmacyInventoriesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routes = useInventoryRoutes();
  const { workSites, workSiteId, centralSite, toast, mode } = usePharmacy();
  const siteOnly = mode === 'site';

  const inventorySites = useMemo(
    () => buildInventorySiteOptions(workSites, centralSite, copy.LOCATION_DUTY),
    [workSites, centralSite]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState(() => (workSiteId ? String(workSiteId) : ''));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (siteOnly) {
      setSiteFilter(workSiteId ? String(workSiteId) : '');
      return;
    }
    const fromQuery = searchParams.get('site');
    if (fromQuery && inventorySites.some((s) => s.key === fromQuery)) {
      setSiteFilter(fromQuery);
    }
  }, [siteOnly, workSiteId, searchParams, inventorySites]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (siteOnly && !workSiteId) {
        setRows([]);
        return;
      }
      const parsed = siteOnly
        ? { siteId: workSiteId, stockLocation: null, isDuty: false }
        : parseInventorySiteKey(siteFilter);

      const params = {
        status: statusFilter || undefined,
        site_id: parsed.siteId || undefined
      };
      // Filtre armoire / pharmacie centrale : isoler MAIN vs DUTY
      if (parsed.isDuty) {
        params.stock_location = 'DUTY';
      } else if (parsed.siteId && centralSite && String(parsed.siteId) === String(centralSite.id)) {
        params.stock_location = 'MAIN';
      }

      const res = await pharmacyService.listInventories(params);
      setRows(res.success ? res.data || [] : []);
    } catch (e) {
      setRows([]);
      toast('danger', e.message || copy.FAIL);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, siteFilter, siteOnly, workSiteId, centralSite, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const list = !search
      ? rows
      : rows.filter((s) => {
          const display = inventorySessionDisplayName(s, copy.LOCATION_DUTY).toLowerCase();
          const hay = `${s.session_number || ''} ${display} ${s.site_name || ''}`.toLowerCase();
          return hay.includes(search);
        });
    return [...list].sort((a, b) => {
      if (a.status === 'COUNTING' && b.status !== 'COUNTING') return -1;
      if (b.status === 'COUNTING' && a.status !== 'COUNTING') return 1;
      return Number(b.id) - Number(a.id);
    });
  }, [rows, search]);

  const stats = useMemo(() => {
    const open = rows.filter((s) => s.status === 'COUNTING').length;
    const closed = rows.filter((s) => s.status === 'CLOSED').length;
    return { total: rows.length, open, closed };
  }, [rows]);

  const openSession = useMemo(
    () => rows.find((s) => s.status === 'COUNTING') || null,
    [rows]
  );

  const selectedCreate = inventorySites.find((s) => s.key === String(createKey)) || null;

  const openCreate = () => {
    if (siteOnly && !workSiteId) {
      toast('warning', copy.NO_WORK_SITE);
      return;
    }
    const nextKey = siteOnly
      ? String(workSiteId)
      : siteFilter ||
        (workSiteId ? String(workSiteId) : inventorySites[0] ? inventorySites[0].key : '');
    setCreateKey(nextKey);
    setCreateNotes('');
    setCreateOpen(true);
  };

  const createSession = async (key, notes) => {
    const parsed = parseInventorySiteKey(key);
    if (!parsed.siteId) {
      toast('warning', copy.CREATE_SITE_REQUIRED);
      return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.createInventory({
        site_id: Number(parsed.siteId),
        notes: notes || undefined,
        stock_location: parsed.isDuty ? 'DUTY' : 'MAIN'
      });
      if (res.success && res.data) {
        toast('success', copy.CREATE_OK);
        setCreateOpen(false);
        navigate(routes.DETAIL(res.data.id));
        return;
      }
      toast('danger', res.message || copy.FAIL);
    } catch (e) {
      toast('warning', e.message || copy.FAIL);
      if (e.open_session_id) {
        setCreateOpen(false);
        navigate(routes.DETAIL(e.open_session_id));
      }
    } finally {
      setBusy(false);
    }
  };

  const openDetail = (session) => navigate(routes.DETAIL(session.id));
  const showSitePicker = !siteOnly && inventorySites.length > 1;

  if (siteOnly && !workSiteId) {
    return (
      <div className="cp-inv-page">
        <PharmacyPageHeader badge={copy.BADGE} title={copy.TITLE} description={copy.DESC_WORK} />
        <div className="cp-empty-state cp-card">
          <span className="cp-empty-state__icon" aria-hidden>
            <Icon name="clipboard-list" size={28} />
          </span>
          <h3 className="cp-empty-state__title">{copy.NO_WORK_SITE}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-inv-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={siteOnly ? copy.DESC_WORK : copy.DESC}
        actions={
          <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate} disabled={busy}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      {openSession && statusFilter !== 'CLOSED' && statusFilter !== 'CANCELLED' ? (
        <button type="button" className="cp-inv-banner" onClick={() => openDetail(openSession)}>
          <span className="cp-inv-banner__icon" aria-hidden>
            <Icon name="clipboard-list" size={16} />
          </span>
          <span className="cp-inv-banner__body">
            <strong>{copy.BANNER_OPEN.replace('{number}', openSession.session_number)}</strong>
            <span>
              {inventorySessionDisplayName(openSession, copy.LOCATION_DUTY) || nextActionLabel('COUNTING')}
            </span>
          </span>
          <span className="cp-inv-banner__cta">
            {copy.BANNER_RESUME}
            <Icon name="chevronRight" size={14} aria-hidden />
          </span>
        </button>
      ) : null}

      <section className="cp-catalog-stats" aria-label="Synthèse inventaires">
        <button
          type="button"
          className={`cp-catalog-stat cp-catalog-stat--clickable${statusFilter === '' ? ' is-active' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </button>
        <button
          type="button"
          className={`cp-catalog-stat cp-catalog-stat--amber cp-catalog-stat--clickable${
            statusFilter === 'COUNTING' ? ' is-active' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'COUNTING' ? '' : 'COUNTING')}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_OPEN}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.open)}</span>
        </button>
        <button
          type="button"
          className={`cp-catalog-stat cp-catalog-stat--ok cp-catalog-stat--clickable${
            statusFilter === 'CLOSED' ? ' is-active' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'CLOSED' ? '' : 'CLOSED')}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_CLOSED}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.closed)}</span>
        </button>
      </section>

      {showSitePicker ? (
        <div className="cp-site-picker" role="group" aria-label={copy.FIELD_SITE}>
          <button
            type="button"
            className={`cp-site-picker__card${siteFilter === '' ? ' is-active' : ''}`}
            onClick={() => setSiteFilter('')}
            aria-pressed={siteFilter === ''}
          >
            <span className="cp-site-picker__body">
              <span className="cp-site-picker__name">{copy.FILTER_ALL}</span>
            </span>
          </button>
          {inventorySites.map((s) => {
            const active = String(s.key) === String(siteFilter);
            return (
              <button
                key={s.key}
                type="button"
                className={`cp-site-picker__card${active ? ' is-active' : ''}${
                  s.isCentral ? ' is-central' : ''
                }`}
                onClick={() => setSiteFilter(String(s.key))}
                aria-pressed={active}
              >
                <span className="cp-site-picker__icon" aria-hidden>
                  <Icon name={s.isDuty ? 'shield' : s.isCentral ? 'warehouse' : 'building'} size={16} />
                </span>
                <span className="cp-site-picker__body">
                  <span className="cp-site-picker__name">{s.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="cp-card">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end">
            <div className="cp-filter-pills" role="group" aria-label={copy.FILTER_STATUS_ARIA}>
              {INVENTORY_STATUSES.map((s) => (
                <button
                  key={s || 'all'}
                  type="button"
                  className={`cp-filter-pill${statusFilter === s ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === '' ? copy.FILTER_ALL : statusLabel(s)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(filteredRows.length)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock label={copy.LOADING} />
          ) : filteredRows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="clipboard-list" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">{copy.EMPTY_HINT}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate} disabled={busy}>
                <Icon name="plus" size={16} aria-hidden />
                {copy.EMPTY_CTA}
              </button>
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table cp-orders-table cp-inv-list-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_NUMBER}</th>
                    {!siteOnly ? <th>{copy.COL_SITE}</th> : null}
                    <th>{copy.COL_PROGRESS}</th>
                    <th>{copy.COL_STARTED}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th className="text-end">{copy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const counted = Number(row.counted_count || 0);
                    const total = Number(row.lines_count || 0);
                    return (
                      <tr
                        key={row.id}
                        className={`cp-orders-table__row${row.status === 'COUNTING' ? ' cp-inv-list-row--open' : ''}`}
                        onClick={() => openDetail(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDetail(row);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                      >
                        <td>
                          <div className="cp-med-cell">
                            <span className="cp-med-cell__icon" aria-hidden>
                              <Icon name="clipboard-list" size={16} />
                            </span>
                            <span className="cp-med-cell__body">
                              <span className="cp-med-cell__name">{row.session_number}</span>
                              <span className="cp-med-cell__desc">{nextActionLabel(row.status)}</span>
                            </span>
                          </div>
                        </td>
                        {!siteOnly ? (
                          <td>{inventorySessionDisplayName(row, copy.LOCATION_DUTY)}</td>
                        ) : null}
                        <td style={{ minWidth: 140 }}>
                          {row.status === 'COUNTING' ? (
                            <InventoryProgress counted={counted} total={total} compact />
                          ) : (
                            <span className="cp-num">
                              {copy.PROGRESS.replace('{counted}', formatNumber(counted)).replace(
                                '{total}',
                                formatNumber(total)
                              )}
                              {total ? ` · ${progressPct(counted, total)} %` : ''}
                            </span>
                          )}
                        </td>
                        <td>{row.started_at ? formatDate(row.started_at) : '—'}</td>
                        <td>
                          <span className={`cp-status-pill ${inventoryStatusClass(row.status)}`}>
                            {statusLabel(row.status)}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className={`cp-btn cp-btn--sm${row.status === 'COUNTING' ? ' cp-btn--primary' : ' cp-btn--ghost'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(row);
                            }}
                          >
                            {row.status === 'COUNTING' ? copy.BANNER_RESUME : copy.OPEN}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={createOpen}
        onHide={() => !busy && setCreateOpen(false)}
        centered
        className="cp-modal cp-modal--inv"
        dialogClassName="cp-inv-modal-dialog"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="clipboard-list" size={18} />
            </span>
            <div>
              <span className="cp-modal__eyebrow">{copy.BADGE}</span>
              <Modal.Title as="h2" className="cp-modal__title">
                {copy.CREATE_TITLE}
              </Modal.Title>
              <p className="cp-modal__desc">{copy.CREATE_DESC}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <ol className="cp-inv-modal-steps" aria-hidden>
            <li className="is-current">
              <span>1</span> {copy.FIELD_SITE}
            </li>
            <li>
              <span>2</span> {copy.FLOW_OPEN}
            </li>
            <li>
              <span>3</span> {copy.FLOW_COUNT}
            </li>
          </ol>

          <div className="cp-inv-modal-panel">
            <Form.Label className="cp-inv-modal-label">{copy.FIELD_SITE}</Form.Label>
            {siteOnly ? (
              <div className="cp-inv-modal-site is-locked">
                <span className="cp-inv-modal-site__icon" aria-hidden>
                  <Icon name="warehouse" size={16} />
                </span>
                <span className="cp-inv-modal-site__name">
                  {selectedCreate?.name || `#${createKey}`}
                </span>
              </div>
            ) : (
              <div className="cp-inv-modal-sites" role="listbox" aria-label={copy.FIELD_SITE}>
                {inventorySites.map((s) => {
                  const active = String(s.key) === String(createKey);
                  return (
                    <button
                      key={s.key}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`cp-inv-modal-site${active ? ' is-active' : ''}`}
                      onClick={() => setCreateKey(s.key)}
                      disabled={busy}
                    >
                      <span className="cp-inv-modal-site__icon" aria-hidden>
                        <Icon name={s.isDuty ? 'shield' : s.isCentral ? 'warehouse' : 'building'} size={16} />
                      </span>
                      <span className="cp-inv-modal-site__name">{s.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="cp-inv-modal-hint">{copy.CREATE_SNAPSHOT}</p>
          </div>

          <Form.Group className="cp-inv-modal-notes">
            <Form.Label className="cp-inv-modal-label">{copy.FIELD_NOTES}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={createNotes}
              placeholder={copy.FIELD_NOTES_PLACEHOLDER}
              onChange={(e) => setCreateNotes(e.target.value)}
              disabled={busy}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button
            type="button"
            className="cp-modal-btn cp-modal-btn--ghost"
            onClick={() => setCreateOpen(false)}
            disabled={busy}
          >
            {copy.CREATE_CANCEL}
          </button>
          <button
            type="button"
            className="cp-modal-btn cp-modal-btn--primary"
            disabled={busy || !createKey}
            onClick={() => createSession(createKey, createNotes)}
          >
            <Icon name="plus" size={16} aria-hidden />
            {copy.CREATE_SUBMIT}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
